import { useCallback, useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import useFlag from "~/config/flags";
import OrderTableWrapper from "~/features/orderTable/OrderTableWrapper";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useGetOrderSummariesQuery } from "~/redux/warehouse/orders.hooks";
import { OrderSummaryDto } from "~/types/api";

import { OrdersSearch } from "./OrdersSearch";
import { OrdersToolbar } from "./OrdersToolbar";
import { selectOrder, setSelectedDate } from "./orders.slice";

type Props = { viewTitle?: ViewNameTranslation };

function byWindowThenName(a: OrderSummaryDto, b: OrderSummaryDto) {
  const aWindow = a.pickingEndTime || new Date();
  const bWindow = b.pickingEndTime || new Date();
  if (aWindow === bWindow) {
    return a.customerName.localeCompare(b.customerName);
  }
  return aWindow < bWindow ? -1 : 1;
}

export function Orders(props: Props) {
  const { viewTitle } = props;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const hasOrdersV2EnhancedFlag =
    useFlag().tablesV2Enhanced.includes("ordersTableV2");
  const { ord_includeSpokes } = useAppSelector(selectServerConfig);
  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const orderFilter = useAppSelector((state) => state.ordersSlice.orderFilter);
  const selectedDate = useAppSelector(
    (state) => state.ordersSlice.selectedDate
  );
  const selectedOrderIds = useAppSelector(
    (state) => state.ordersSlice.selectedOrderIds
  );
  const orderStatusFilters = useAppSelector(
    (state) => state.ordersSlice.orderStatusFilters
  );
  const orderTypeFilters = useAppSelector(
    (state) => state.ordersSlice.orderTypeFilters
  );

  const handleOrderSelected = useCallback(
    (order: OrderSummaryDto) => {
      dispatch(selectOrder(order.orderId));
    },
    [dispatch]
  );

  const {
    data: orders,
    error: ordersError,
    isLoading: isOrdersLoading
  } = useGetOrderSummariesQuery(
    {
      date: selectedDate,
      fcTimeZone: fulfillmentCenter?.timeZone,
      includeSpokes: ord_includeSpokes,
      search: orderFilter,
      statuses: orderStatusFilters,
      orderTypes: orderTypeFilters
    },
    // TODO: refetchOnMountOrArgChange can be removed when batch creation invalidates orders through rtk query
    { refetchOnMountOrArgChange: true }
  );

  const handleDateChange = useCallback(
    (date: Date | null): void => {
      dispatch(setSelectedDate(date || null));
    },
    [dispatch]
  );

  const datePicker = useMemo(() => {
    return { selectedDate, onDateChange: handleDateChange };
  }, [selectedDate, handleDateChange]);

  const ordersSearchBar = useMemo(() => <OrdersSearch />, []);

  const { setToolbar } = useNavbar({
    centerComponent: hasOrdersV2EnhancedFlag ? undefined : ordersSearchBar,
    datePicker: hasOrdersV2EnhancedFlag ? undefined : datePicker,
    viewTitle
  });

  useEffect(() => {
    if (selectedOrderIds.length) {
      setToolbar(<OrdersToolbar />);
    } else {
      setToolbar(undefined);
    }
  }, [selectedOrderIds.length, setToolbar]);

  useEffect(() => {
    if (!selectedDate) {
      dispatch(setSelectedDate(new Date()));
    }
  }, [dispatch, selectedDate]);

  return (
    <OrderTableWrapper
      fulfillmentCenter={fulfillmentCenter}
      onOrderSelected={handleOrderSelected}
      orders={orders?.slice().sort(byWindowThenName) || []}
      ordersLoading={isOrdersLoading}
      selectedDate={selectedDate}
      selectedOrderIds={selectedOrderIds}
      title={t("orders")}
      ordersError={ordersError}
      handleDateChange={handleDateChange}
      hasOrdersV2EnhancedFlag={hasOrdersV2EnhancedFlag}
    />
  );
}
